/**
 * DPR-aware Scale.RESIZE: the canvas backing store is CSS size × effective DPR while its CSS
 * box stays 100% of the parent, so text and vector art stay crisp on phones and 4K screens.
 * Game coordinates are device pixels; `vp.u` converts 1 CSS px to device px.
 */
import * as Phaser from 'phaser';

export const vp = {
  /** effective device pixel ratio (capped for fill-rate on large screens) */
  dpr: 1,
  cssW: 1,
  cssH: 1,
  get u(): number {
    return this.dpr;
  },
};

const MAX_BACKING_PIXELS = 4_200_000;

export function effectiveDpr(cssW: number, cssH: number): number {
  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
  const cap = Math.sqrt(MAX_BACKING_PIXELS / Math.max(1, cssW * cssH));
  // 2× is visually indistinguishable at arm's length and halves fill-rate on 2.6×+ phones
  return Math.max(1, Math.min(dpr, 2, cap));
}

type ScaleInternals = {
  parent: HTMLElement | null;
  parentSize: Phaser.Structs.Size;
};

let installed = false;

export function installDprScale(): void {
  if (installed) return;
  installed = true;
  const proto = Phaser.Scale.ScaleManager.prototype as unknown as { getParentBounds: () => boolean };
  proto.getParentBounds = function (this: ScaleInternals): boolean {
    if (!this.parent) return false;
    const rect = this.parent.getBoundingClientRect();
    vp.cssW = Math.max(1, rect.width);
    vp.cssH = Math.max(1, rect.height);
    vp.dpr = effectiveDpr(vp.cssW, vp.cssH);
    const w = Math.round(vp.cssW * vp.dpr);
    const h = Math.round(vp.cssH * vp.dpr);
    if (this.parentSize.width !== w || this.parentSize.height !== h) {
      this.parentSize.setSize(w, h);
      return true;
    }
    return false;
  };
}

/** Canvas device px → CSS px relative to the page (canvas is full-bleed at 0,0). */
export function toCss(x: number, y: number): { x: number; y: number } {
  return { x: x / vp.dpr, y: y / vp.dpr };
}
